import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * PushClickTracker — records one click per (user, campaign) when the app
 * boots at a URL that includes `?c=<campaign_id>` (added by the push
 * broadcast endpoint). After posting the beacon, the `c` param is stripped
 * from the URL so the analytics record is only written once per open.
 *
 * Auth is required by the backend `/api/push/click` endpoint — if the user
 * isn't signed in yet, this is a no-op and the query param stays behind so
 * a later render (post-login) can still record the click.
 */
const PushClickTracker = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(location.search);
    const campaign = params.get('c');
    if (!campaign) return;

    const token = localStorage.getItem('rbr_token');
    if (!token) return;  // Wait until we're signed in — see docstring.

    // Deterministic per-open token so a rapid double-render doesn't count
    // twice (backend also enforces this via dedupeId).
    const openToken = (() => {
      try {
        const k = `rbr_push_click:${campaign}`;
        let t = sessionStorage.getItem(k);
        if (!t) { t = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`; sessionStorage.setItem(k, t); }
        return t;
      } catch { return `${Date.now()}`; }
    })();

    fetch(`${API_URL}/api/push/click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ campaign, token: openToken }),
    }).catch(() => { /* analytics never blocks UX */ });

    // Strip the c= param from the URL so we don't record on every re-render.
    params.delete('c');
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
        hash: location.hash,
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return null;
};

export default PushClickTracker;
